import { useState, useEffect, useRef, useCallback } from "react";
import { useUpdateEffect } from "react-use";
import { Modal, Box, useTheme, Typography } from "@mui/material";
import { CustomTypography } from "../custom-typography/custom-typography";
import { CancelIcon } from "../../assets/icon";
import {
  getRealtimeDialogs,
  getRealtimeStatistics,
} from "../../axios/realtimeSpeech";
import {
  RealtimeDialog,
  RealtimeStatisticsItem,
} from "../../types/realtimeSpeech";
import { TranscriptPanel } from "./transcript-panel/transcript-panel";
import { useAppStore } from "../../stores/appStore";
import {
  modalBoxStyles,
  headerStyles,
  contentStyles,
  blockHeadingStyles,
  statisticsContainerStyles,
  statisticsItemContainerStyles,
  statisticsItemStyles,
  statisticsItemValueStyles,
} from "./styles";
import { RecordingBlock } from "./recording-block/recording-block";

interface RecordingsModalProps {
  open: boolean;
  onClose: () => void;
}

export const RecordingsModal = ({ open, onClose }: RecordingsModalProps) => {
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  const [realtimeDialogs, setRealtimeDialogs] = useState<RealtimeDialog[]>([]);
  const [realtimeStatistics, setRealtimeStatistics] = useState<
    RealtimeStatisticsItem[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [openTranscriptIndex, setOpenTranscriptIndex] = useState<number | null>(
    null,
  );
  const [playbackErrors, setPlaybackErrors] = useState<Record<string, string>>(
    {},
  );

  const previousSellingDialogueRef = useRef<boolean>(false);
  const previousLiveSpeechModeRef = useRef<string | null>(null);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isCoachModeActiveRef = useRef<boolean>(false);
  const shouldPollRef = useRef<boolean>(false);
  const coachRecordIdRef = useRef<string | null>(null);

  const {
    focus,
    domain,
    sellingDialogue,
    liveSpeechMode,
    realtimeMenuOptions,
  } = useAppStore();

  const theme = useTheme();

  // Close Highlights automatically when entering coaching mode.
  useEffect(() => {
    if (sellingDialogue && liveSpeechMode === "coach_me") {
      setOpenTranscriptIndex(null);
    }
  }, [sellingDialogue, liveSpeechMode]);

  const fetchRealtimeDialogs = useCallback(
    async (opts?: { silent?: boolean }) => {
      const silent = Boolean(opts?.silent);
      if (!silent) setLoading(true);
      try {
        const [dialogs, statistics] = await Promise.all([
          getRealtimeDialogs(),
          getRealtimeStatistics(),
        ]);
        setRealtimeDialogs(dialogs || []);
        setRealtimeStatistics(statistics || []);
      } catch (error) {
        let errorMessage = "Unknown error";
        if (error instanceof Error) {
          errorMessage = error.message;
        } else if (typeof error === "string") {
          errorMessage = error;
        }

        console.error(errorMessage);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (open) {
      fetchRealtimeDialogs({ silent: false });
      setPlaybackErrors({});
    }
  }, [domain, focus, open, fetchRealtimeDialogs]);

  // Refresh history/statistics when an async roleplay finishes.
  useEffect(() => {
    const handleAsyncRoleplayFinished = () => {
      void fetchRealtimeDialogs({ silent: !open });
    };

    window.addEventListener(
      "asyncRoleplayFinished",
      handleAsyncRoleplayFinished,
    );

    return () => {
      window.removeEventListener(
        "asyncRoleplayFinished",
        handleAsyncRoleplayFinished,
      );
    };
  }, [fetchRealtimeDialogs, open]);

  // Track when coaching mode is active
  useEffect(() => {
    if (liveSpeechMode === "coach_me" && sellingDialogue) {
      isCoachModeActiveRef.current = true;
      const rid = (realtimeMenuOptions as any)?.record_id;
      if (typeof rid === "string" && rid.trim()) {
        coachRecordIdRef.current = rid;
      }
    }
  }, [liveSpeechMode, sellingDialogue, realtimeMenuOptions]);

  useUpdateEffect(() => {
    const wasSellingDialogue = previousSellingDialogueRef.current;
    const isSellingDialogue = sellingDialogue;
    const wasCoachMode =
      previousLiveSpeechModeRef.current === "coach_me" ||
      isCoachModeActiveRef.current;

    if (wasSellingDialogue && !isSellingDialogue && open && wasCoachMode) {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }

      shouldPollRef.current = true;

      const shouldStopPolling = (dialogs: RealtimeDialog[]) => {
        const targetId = coachRecordIdRef.current;
        if (!targetId) return false;
        const target = dialogs.find((d) => d?.id === targetId);
        const coachingPath =
          typeof (target as any)?.coaching_path === "string"
            ? (target as any).coaching_path.trim()
            : "";
        return coachingPath.length > 0;
      };

      const silentRefreshOnce = async () => {
        try {
          const [dialogs, statistics] = await Promise.all([
            getRealtimeDialogs(),
            getRealtimeStatistics(),
          ]);
          const safeDialogs: RealtimeDialog[] = dialogs || [];
          setRealtimeDialogs(safeDialogs);
          setRealtimeStatistics(statistics || []);
          if (shouldStopPolling(safeDialogs)) {
            shouldPollRef.current = false;
            if (refreshIntervalRef.current) {
              clearInterval(refreshIntervalRef.current);
              refreshIntervalRef.current = null;
            }
          }
        } catch (error) {
          console.error("Error refreshing recordings after coaching:", error);
        }
      };

      refreshTimeoutRef.current = setTimeout(() => {
        silentRefreshOnce();
      }, 1500);

      let pollCount = 0;
      refreshIntervalRef.current = setInterval(() => {
        pollCount++;
        if (pollCount <= 10 && shouldPollRef.current && open) {
          silentRefreshOnce();
        } else {
          if (refreshIntervalRef.current) {
            clearInterval(refreshIntervalRef.current);
            refreshIntervalRef.current = null;
          }
          shouldPollRef.current = false;
        }
      }, 3000);

      isCoachModeActiveRef.current = false;
    }

    previousSellingDialogueRef.current = isSellingDialogue;
    previousLiveSpeechModeRef.current = liveSpeechMode;
  }, [sellingDialogue, open, liveSpeechMode]);

  useEffect(() => {
    if (!open) {
      shouldPollRef.current = false;
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, []);

  const handleAudioError = (id: string) => {
    setPlaybackErrors((prev) => {
      if (prev[id]) return prev;
      return {
        ...prev,
        [id]: "Playback failed. The audio could not be loaded or timed out.",
      };
    });
  };

  const handleClearPlaybackError = (id: string) => {
    setPlaybackErrors((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleClose = () => {
    setOpenTranscriptIndex(null);
    onClose();
  };

  const safeTranscript =
    openTranscriptIndex != null &&
    openTranscriptIndex >= 0 &&
    openTranscriptIndex < realtimeDialogs.length
      ? realtimeDialogs[openTranscriptIndex].transcript
      : null;

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <Box sx={modalBoxStyles}>
          <Box sx={headerStyles}>
            <CustomTypography fontSize={20} fontWeight={600} color="#FFFFFF">
              Roleplays
            </CustomTypography>
            <CancelIcon
              color="#FFFFFF"
              onClick={handleClose}
              style={{ cursor: "pointer" }}
            />
          </Box>
          <Box
            sx={contentStyles}
            onClick={(e) => {
              // Close highlights if clicking on empty space or recording blocks
              const target = e.target as HTMLElement;
              if (
                !target.closest("button") &&
                !target.closest("select") &&
                !target.closest("[role='button']") &&
                !target.closest(".MuiSelect-root") &&
                !target.closest(".MuiButton-root") &&
                !target.closest(".MuiFormControl-root")
              ) {
                if (openTranscriptIndex !== null) {
                  setOpenTranscriptIndex(null);
                }
              }
            }}
          >
            {loading && (
              <CustomTypography
                fontSize={20}
                fontWeight={600}
                color={theme.palette.modalColors.colorLable}
              >
                Loading recordings...
              </CustomTypography>
            )}

            {realtimeStatistics.length > 0 && (
              <>
                <Typography sx={blockHeadingStyles}>Statistics:</Typography>
                <Box sx={statisticsContainerStyles}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "16px",
                      width: "100%",
                      minWidth: 0,
                      maxWidth: "100%",
                    }}
                  >
                    {realtimeStatistics
                      .filter((item) =>
                        item.text.toLowerCase().includes("average"),
                      )
                      .map((item) => {
                        const baseStyles =
                          typeof statisticsItemValueStyles === "function"
                            ? statisticsItemValueStyles(theme)
                            : statisticsItemValueStyles;
                        const valueColor = item.option_color
                          ? item.option_color
                          : theme.palette.primary.main;
                        return (
                          <Box
                            key={item.text}
                            sx={statisticsItemContainerStyles}
                          >
                            <Typography sx={statisticsItemStyles}>
                              {item.text}
                            </Typography>
                            <Typography
                              sx={{
                                ...baseStyles,
                                color: valueColor,
                              }}
                            >
                              {item.value}
                            </Typography>
                          </Box>
                        );
                      })}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "16px",
                      width: "100%",
                      minWidth: 0,
                      maxWidth: "100%",
                    }}
                  >
                    {realtimeStatistics
                      .filter((item) =>
                        item.text.toLowerCase().includes("time"),
                      )
                      .map((item) => (
                        <Box key={item.text} sx={statisticsItemContainerStyles}>
                          <Typography sx={statisticsItemStyles}>
                            {item.text}
                          </Typography>
                          <Typography sx={statisticsItemValueStyles}>
                            {item.value}
                          </Typography>
                        </Box>
                      ))}
                  </Box>
                </Box>
              </>
            )}

            <Typography sx={blockHeadingStyles}>Recordings:</Typography>

            {!loading && (!realtimeDialogs || realtimeDialogs.length === 0) ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  padding: 4,
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{ fontWeight: 600 }}
                >
                  No recordings available
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ maxWidth: "300px" }}
                >
                  Audio recordings will appear here once they are created
                </Typography>
              </Box>
            ) : (
              realtimeDialogs.map((recording, index) => (
                <RecordingBlock
                  key={recording.id ?? index}
                  recording={recording}
                  isPlaying={playingIndex === index}
                  setIsPlaying={(value: boolean) =>
                    setPlayingIndex(value ? index : null)
                  }
                  playbackError={playbackErrors[recording.id]}
                  onAudioError={() => handleAudioError(recording.id)}
                  onClearPlaybackError={() =>
                    handleClearPlaybackError(recording.id)
                  }
                  onOpenTranscript={() => setOpenTranscriptIndex(index)}
                  isHighlightsOpen={openTranscriptIndex === index}
                  onCloseHighlights={() => setOpenTranscriptIndex(null)}
                />
              ))
            )}
          </Box>
        </Box>
      </Modal>

      {open && (
        <TranscriptPanel
          transcript={safeTranscript}
          onHide={() => setOpenTranscriptIndex(null)}
        />
      )}
    </>
  );
};
